import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Calendar } from 'lucide-react';
import Section from './Section';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&h=400&fit=crop';

/**
 * LatestFromBlog, 3-card strip pulling the newest blog posts from /api/blog.
 * Renders nothing while loading or if the API returns no posts (fail-quiet).
 * Drop into any v2 page above the final CTA for an internal-linking SEO win.
 */
export default function LatestFromBlog({
  heading = 'Latest from the blog',
  eyebrow = 'Read next',
  subheading = 'Straight-talking guides on SEO, websites, podcasts, YouTube and content that actually gets found.',
  bg = 'white',
  limit = 3,
  testId = 'latest-from-blog',
}) {
  const [posts, setPosts] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/blog?limit=${limit}`);
        if (!cancelled && Array.isArray(res.data)) {
          setPosts(res.data.slice(0, limit));
        }
      } catch (e) {
        /* fail quiet, component just hides */
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [limit]);

  if (!ready || posts.length === 0) return null;

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <Section bg={bg} testId={testId}>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
        <div className="max-w-2xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">
            {eyebrow}
          </p>
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">
            {heading}
          </h2>
          <p className="text-[#64748B] mt-3 leading-relaxed">{subheading}</p>
        </div>
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E2A44] hover:text-[#BC516F] transition shrink-0"
          data-testid={`${testId}-view-all`}
        >
          View all articles <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {posts.map((post, i) => (
          <Link
            key={post.id || post.slug || i}
            to={`/blog/${post.slug}`}
            className="group bg-white border border-[#E2E8F0] rounded-[14px] overflow-hidden shadow-[0_4px_16px_rgba(30,42,68,0.04)] hover:shadow-[0_14px_36px_rgba(30,42,68,0.10)] hover:-translate-y-0.5 transition-all"
            data-testid={`${testId}-card-${i}`}
          >
            <div className="aspect-[16/10] overflow-hidden bg-[#F6F8FB]">
              <img
                src={post.featured_image}
                alt={post.image_alt || post.title}
                title={post.image_title || post.title}
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  e.target.src = FALLBACK_IMAGE;
                }}
              />
            </div>
            <div className="p-6">
              {(post.tags || []).slice(0, 1).map((tag) => (
                <span
                  key={tag}
                  className="inline-block text-xs font-semibold uppercase tracking-wider text-[#BC516F] mb-2"
                >
                  {tag}
                </span>
              ))}
              <h3 className="font-display font-semibold text-[#1E2A44] text-lg leading-snug line-clamp-2">
                {post.title}
              </h3>
              <p className="text-[#64748B] text-sm mt-2 line-clamp-2 leading-relaxed">
                {post.excerpt}
              </p>
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#E2E8F0]">
                <span className="text-xs text-[#64748B] inline-flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" /> {formatDate(post.created_at)}
                </span>
                <span className="text-sm font-semibold text-[#1E2A44] inline-flex items-center gap-1 group-hover:text-[#BC516F] transition">
                  Read <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </Section>
  );
}
